import React, { useState, useEffect } from 'react';
import { BarChart3, FileText, TrendingUp, Target, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import './AnalysisViewer.css';

const AnalysisViewer = () => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState(null);

  useEffect(() => {
    fetchAnalysisData();
  }, []);

  const fetchAnalysisData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAnalysisData();
      setAnalysisData(response.data);
    } catch (error) {
      toast.error('Failed to load analysis data');
    } finally {
      setLoading(false);
    }
  };

  const exportAnalysis = () => {
    if (analysisData) {
      const dataStr = JSON.stringify(analysisData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'proposal_analysis_report.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Analysis report exported successfully');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading analysis data...</div>
      </div>
    );
  }

  if (!analysisData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">No analysis data available</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Analysis Viewer</h1>
            <p className="page-subtitle">Comprehensive analysis of proposal performance and patterns</p>
          </div>
          <button onClick={exportAnalysis} className="btn btn-secondary">
            <Download className="w-4 h-4" />
            Export Report
          </button>
        </div>
      </div>
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Total Proposals</p>
              <p className="text-2xl font-semibold text-slate-100">{analysisData.totalProposals}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Analyzed</p>
              <p className="text-2xl font-semibold text-slate-100">{analysisData.analyzedProposals}</p>
            </div>
            <BarChart3 className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Avg. Score</p>
              <p className="text-2xl font-semibold text-slate-100">{analysisData.averageScore}/10</p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Success Rate</p>
              <p className="text-2xl font-semibold text-slate-100">
                {analysisData.totalProposals > 0 ? Math.round((analysisData.analyzedProposals / analysisData.totalProposals) * 100) : 0}%
              </p>
            </div>
            <Target className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>
      {/* Top Performing Proposals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Top Performing Proposals</h2>
          <div className="space-y-4">
            {analysisData.topPerformingProposals && analysisData.topPerformingProposals.map((proposal, idx) => (
              <div
                key={idx}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer"
                onClick={() => setSelectedProposal(proposal)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-slate-100">{proposal.name || proposal.clientName}</h3>
                  {proposal.score && <span className="text-sm font-semibold text-green-600">{proposal.score}/10</span>}
                </div>
                {proposal.date && <p className="text-sm text-slate-400 mb-2">{proposal.date}</p>}
                {proposal.insights && (
                  <div className="flex flex-wrap gap-1">
                    {proposal.insights.slice(0, 2).map((insight, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                      >
                        {insight}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* Success Factors */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Success Factors</h2>
          <div className="space-y-4">
            {analysisData.structuralStats && analysisData.structuralStats.successFactors && analysisData.structuralStats.successFactors.map((factor, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-slate-300">{factor.factor}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${factor.impact * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-slate-100">
                    {Math.round(factor.impact * 100)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Common Sections */}
      <div className="mt-8">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Common Sections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {analysisData.structuralStats && analysisData.structuralStats.commonSections && analysisData.structuralStats.commonSections.map((section, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-slate-100">{section.name}</h3>
                  <span className="text-sm font-semibold text-blue-600">{section.frequency}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${section.frequency}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Pattern Analysis */}
      <div className="mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-slate-100 mb-4">Content Patterns</h2>
            <div className="space-y-4">
              {analysisData.patternAnalysis && analysisData.patternAnalysis.contentPatterns && analysisData.patternAnalysis.contentPatterns.map((pattern, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">{pattern.pattern}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full"
                        style={{ width: `${pattern.success * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-slate-100">
                      {Math.round(pattern.success * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-slate-100 mb-4">Visual Patterns</h2>
            <div className="space-y-4">
              {analysisData.patternAnalysis && analysisData.patternAnalysis.visualPatterns && analysisData.patternAnalysis.visualPatterns.map((pattern, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">{pattern.pattern}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-orange-500 h-2 rounded-full"
                        style={{ width: `${pattern.effectiveness * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-slate-100">
                      {Math.round(pattern.effectiveness * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Selected Proposal Modal */}
      {selectedProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-600 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto backdrop-blur-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-100">{selectedProposal.name || selectedProposal.clientName}</h2>
              <button
                onClick={() => setSelectedProposal(null)}
                className="text-slate-400 hover:text-slate-200"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                {selectedProposal.score && <h3 className="font-medium text-slate-100 mb-2">Score: {selectedProposal.score}/10</h3>}
                {selectedProposal.date && <p className="text-sm text-slate-400">Date: {selectedProposal.date}</p>}
              </div>
              <div>
                <h3 className="font-medium text-slate-100 mb-2">Key Insights:</h3>
                <ul className="list-disc list-inside space-y-1">
                  {selectedProposal.insights && selectedProposal.insights.map((insight, index) => (
                    <li key={index} className="text-sm text-slate-300">{insight}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisViewer; 